import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getFieldName, paginate } from "../../scripts/helperScripts";
import { getReferrals, getUserProgramUnits } from "../services/soar";
import Input from "./Input";
import Pagination from "./Pagination";
import ReferralDecisionModal from "./ReferralDecisionModal";
import ReferralModal from "./ReferralModal";
import SearchBox from "./SearchBox";
import Table from "./Table";
import ToggleSwitch from "./ToggleSwitch";

function ReferralManagement() {
  const [data, setData] = useState({
    StatusFilter: "Pending",
    SearchQuery: "",
    BeginDate: "",
    EndDate: "",
  });
  const [expanded, setExpanded] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [programUnits, setProgramUnits] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [referralData, setReferralData] = useState({});
  const [dynamicData, setDynamicData] = useState({});
  const [referralStatus, setReferralStatus] = useState("");
  const [isReferralDecisionOpen, setIsReferralDecisionOpen] = useState(false);
  const [controllerSignal, setControllerSignal] = useState();

  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState({
    path: "CreatedOn",
    order: "desc",
  });

  const pageSize = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
    if (!safyInfo?.isAuthenticated) navigate("/");
  });

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (controllerSignal && !controllerSignal.aborted) {
      const loadProgramUnits = async () => {
        const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

        const response = await getUserProgramUnits(
          safyInfo.user.safyInfo.BusinessUnitIdName
        );

        if (!response || controllerSignal.aborted) {
          return;
        }

        setProgramUnits(response);
      };

      loadProgramUnits();
    }
  }, [controllerSignal]);

  const loadReferrals = useCallback(async () => {
    if (controllerSignal && !controllerSignal.aborted) {
      const toastId = toast.loading("Loading Referrals...", {
        type: toast.TYPE.INFO,
      });
      const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
      var org =
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "ReactLive"
          : "ReactDev";

      const response = await getReferrals(org, controllerSignal);

      if (!response || controllerSignal.aborted) {
        toast.dismiss(toastId);
        return;
      }

      const result = JSON.parse(response);

      if (result.SystemResponse?.[0].Code === "FAIL") {
        toast.update(toastId, {
          render: "Error loading Referrals",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        return;
      }

      if (!result.Referrals?.Referral) {
        toast.update(toastId, {
          render: "No Referrals found",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        return;
      }

      let refs = result.Referrals.Referral.filter((ref) =>
        safyInfo.user.divisions.includes(
          ref.ProposedProgramName.split(" - ")[0]
        )
      );
      setReferrals(refs);
      toast.update(toastId, {
        render: "Referrals loaded successfully.",
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    }
  }, [controllerSignal]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  const programUnitOptions = useMemo(() => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

    const options = programUnits
      .filter(
        (programUnit) =>
          !programUnit.ccx_expdate &&
          getFieldName(programUnit.ccx_programtype) !== "Foster Home" &&
          safyInfo.user.divisions.includes(
            getFieldName(programUnit.ccx_facility)
          )
      )
      .map((programUnit) => ({
        _id: programUnit.ccx_unitid.replace(/[{}]/g, ""),
        name: programUnit.ccx_name,
        state: getFieldName(programUnit["facility.ccx_agency"]).split(
          " of "
        )[1],
        referralConfig: programUnit.sor_referralconfig
          ? JSON.parse(programUnit.sor_referralconfig)
          : { referral: {} },
      }));

    return options;
  }, [programUnits]);

  const handleShowModal = () => {
    setIsOpen(true);
  };

  const handleHideModal = () => {
    setIsOpen(false);
  };

  const showAcceptModal = (referral) => {
    setReferralData(referral);
    setDynamicData(referral.DynamicFields.results);
    setReferralStatus("Accepted");
    setIsReferralDecisionOpen(true);

    if (referral.DynamicFields !== "" && referral.DynamicFields.results) {
      setDynamicData(referral.DynamicFields.results);
    }
  };

  const showDeferModal = (referral) => {
    setReferralData(referral);
    setDynamicData(referral.DynamicFields.results);
    setReferralStatus("Deferred");
    setIsReferralDecisionOpen(true);
  };

  const filterBySearchQuery = (dataObj, referral) => {
    if (dataObj.SearchQuery === "") {
      return true;
    }

    return (
      `${referral.FirstName}${
        referral.MiddleName ? ` ${referral.MiddleName}` : ""
      } ${referral.LastName}`
        .toLowerCase()
        .includes(dataObj.SearchQuery.toLowerCase()) ||
      referral.ProposedProgramName.toLowerCase().includes(
        dataObj.SearchQuery.toLowerCase()
      )
    );
  };

  const filterByBeginDate = (dataObj, referral) => {
    if (dataObj.BeginDate === "") {
      return true;
    }

    const referralDate = moment(referral.DateofReferral);
    const beginDate = moment(dataObj.BeginDate);

    return referralDate.isSameOrAfter(beginDate);
  };

  const filterByEndDate = (dataObj, referral) => {
    if (dataObj.EndDate === "") {
      return true;
    }

    const referralDate = moment(referral.DateofReferral);
    const endDate = moment(dataObj.EndDate);

    return referralDate.isSameOrBefore(endDate);
  };

  const filterByStatus = (dataObj, referral) => {
    return referral.Status === dataObj.StatusFilter;
  };

  const filteredReferrals = useMemo(() => {
    if (!referrals || referrals.length === 0) {
      return [];
    }

    const referralFilter = (dataObj, referral) => {
      return (
        filterBySearchQuery(dataObj, referral) &&
        filterByBeginDate(dataObj, referral) &&
        filterByEndDate(dataObj, referral) &&
        filterByStatus(dataObj, referral)
      );
    };

    const filtered = referrals.filter((referral) =>
      referralFilter(data, referral)
    );

    return filtered;
  }, [referrals, data]);

  const totalCount = useMemo(() => {
    return filteredReferrals.length;
  }, [filteredReferrals]);

  const pagedReferrals = useMemo(() => {
    const sorted = _.orderBy(
      filteredReferrals,
      [sortColumn.path],
      [sortColumn.order]
    );
    return paginate(sorted, currentPage, pageSize);
  }, [filteredReferrals, sortColumn, currentPage, pageSize]);

  const handleToggleChange = (value) => {
    const obj = { ...data };
    obj.StatusFilter = value;
    setData(obj);
  };

  const handleHideReferralDecisionModal = () => {
    setIsReferralDecisionOpen(false);
  };

  const handleChange = ({ currentTarget: input }) => {
    const obj = { ...data };
    obj[input.name] = input.value;
    setData(obj);
    setCurrentPage(1);
  };

  const columns = useMemo(() => {
    const defaultColumns = [
      {
        key: "Name",
        path: "FirstName",
        label: "Name",
        content: (referral) =>
          `${referral.FirstName}${
            referral.MiddleName ? ` ${referral.MiddleName}` : ""
          } ${referral.LastName}`,
      },
      {
        key: "ProposedProgram",
        path: "ProposedProgramName",
        label: "Proposed Program",
        content: (referral) => {
          const proposedProgram = programUnitOptions.filter(
            (option) => option._id === referral.ProposedProgram
          )[0];
          return proposedProgram?.name;
        },
      },
      {
        key: "ReferralDate",
        path: "DateofReferral",
        label: "Referral Date",
        content: (referral) =>
          moment.utc(referral.DateofReferral).startOf("day").format("ll"),
      },
      {
        key: "DecisionDate",
        path: "StatusChangeDate",
        label: "Decision Date",
        hideColumn: data.StatusFilter === "Pending",
        content: (referral) =>
          moment.utc(referral.StatusChangeDate).startOf("day").format("ll"),
      },
      {
        key: "CreatedOn",
        path: "CreatedOn",
        label: "Created On",
        content: (referral) =>
          moment.utc(referral.CreatedOn).startOf("day").format("ll"),
      },
      {
        key: "Edit",
        content: (referral) => (
          <div style={{ minWidth: "180px" }}>
            <div className="d-flex flex-column flex-md-row">
              <button
                className="btn btn-sm btn-safy-info mb-1 mb-md-0 me-md-1 w-100"
                onClick={() => navigate(`/referral/${referral.ReferralId}`)}
              >
                {referral.Status === "Pending" && <>Edit</>}
                {referral.Status !== "Pending" && <>View</>}
              </button>
              {referral.Status === "Pending" && (
                <>
                  <button
                    className="btn btn-sm btn-success mb-1 mb-md-0 me-md-1 w-100"
                    onClick={() => showAcceptModal(referral)}
                  >
                    Accept
                  </button>
                  <button
                    className="btn btn-sm btn-danger mb-1 mb-md-0 w-100"
                    onClick={() => showDeferModal(referral)}
                  >
                    Defer
                  </button>
                </>
              )}
              {referral.DynamicFields?.results?.notes?.text && (
                <div className="ms-md-1 text-center">
                  <i
                    className="fas fa-comment-alt text-warning fa-lg"
                    title={referral.DynamicFields.results.notes.text}
                  ></i>
                </div>
              )}
            </div>
          </div>
        ),
      },
    ];

    if (data.StatusFilter === "Pending" || data.StatusFilter === "Accepted") {
      return defaultColumns;
    }

    defaultColumns.push({
      key: "Deferred",
      content: (referral) => (
        <>
          {referral.Status === "Deferred" &&
          referral.DynamicFields &&
          referral.DynamicFields.results
            ? referral.DynamicFields.results.deferralReasons === undefined
              ? referral.DeferralReason
              : referral.DynamicFields.results.deferralReasons.text &&
                referral.DynamicFields.results.deferralReasons.text !== ""
              ? referral.DynamicFields.results.deferralReasons.text
              : "No reason provided"
            : null}
        </>
      ),
    });

    return defaultColumns;
  }, [data.StatusFilter, programUnitOptions, navigate]);

  return (
    <>
      <ReferralDecisionModal
        isOpen={isReferralDecisionOpen}
        referralData={referralData}
        setReferralData={setReferralData}
        dynamicData={dynamicData}
        setDynamicData={setDynamicData}
        referralStatus={referralStatus}
        programUnitOptions={programUnitOptions}
        programUnits={programUnits}
        hideModal={handleHideReferralDecisionModal}
        updateReferrals={loadReferrals}
      ></ReferralDecisionModal>
      <div className="border p-2 mb-4 mt-4 card-safy">
        <div className="h4">
          <i
            className={
              expanded
                ? "fas fa-chevron-up p-1 text-primary"
                : "fas fa-chevron-down p-1 text-primary"
            }
            onClick={() => setExpanded(!expanded)}
          ></i>
          Referral
        </div>
        {expanded && (
          <>
            <div className="clearfix mb-4">
              <div className="row">
                <div className="col-auto pt-2">
                  <button className="btn btn-primary" onClick={handleShowModal}>
                    Add Referral
                  </button>
                </div>
                <div className="col pt-2">
                  <SearchBox
                    name="SearchQuery"
                    placeholder="Search by name or program..."
                    value={data.SearchQuery}
                    onChange={handleChange}
                    clearSearch={() =>
                      setData((d) => ({ ...d, SearchQuery: "" }))
                    }
                  />
                </div>
                <div className="col-auto">
                  <Input
                    name="BeginDate"
                    label="Begin Date:"
                    value={data.BeginDate}
                    onChange={handleChange}
                    type="date"
                  />
                </div>
                <div className="col-auto">
                  <Input
                    name="EndDate"
                    label="End Date:"
                    value={data.EndDate}
                    onChange={handleChange}
                    type="date"
                  />
                </div>
                <div className="col-auto pt-2">
                  <div style={{ width: "210px" }}>
                    <ToggleSwitch
                      values={["Pending", "Accepted", "Deferred"]}
                      selected={data.StatusFilter}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
            {programUnits.length > 0 ? (
              <>
                <Table
                  columns={columns}
                  data={pagedReferrals}
                  sortColumn={sortColumn}
                  onSort={setSortColumn}
                />
                <div className="row justify-content-center">
                  <Pagination
                    itemsCount={totalCount}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            ) : null}
            {isOpen ? (
              <ReferralModal
                isOpen={isOpen}
                hideModal={handleHideModal}
                programUnitOptions={programUnitOptions}
                updateReferrals={loadReferrals}
              ></ReferralModal>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

export default ReferralManagement;
